import { isValidNumber } from "ramda-adjunct";
import { useEffect, useRef, useState } from "react";
import { useSpring, animated } from "react-spring";

// functions
import { useFirestoreData, increaseDemoData, resetDemoData } from "./firebase";

// styles
import "./App.scss";
import { useDebounce } from "./useDebounce";
import { Option } from "./Option";
import { createFlyingBunAnimationConfig } from "./createFlyingBunAnimationConfig";
import { createFlyingBunStyle } from "./createFlyingBunStyle";
import { createLogoAnimationConfig } from "./createLogoAnimationConfig";
import { BunQueue } from "./BunQueue";

const fsPath = `RealtimeTransactionsCount/lussen${window.location.pathname.length > 2 ? '/store' + window.location.pathname : ''}`
const product1 = "6437005070266"
const product2 = "659"
const chain = window.location.pathname.length > 2 ? "store" : "2";

const mode = "prod"; // "demo" | "test" | "prod"
const isDemo = mode === "demo";

const App = () => {
  const [delta, setDelta] = useState({})
  const { loading, data, delta: currentDelta } = useFirestoreData(fsPath, mode === "prod" ? chain : mode, [product1, product2], setDelta);
  const [debouncedData, setDebounced] = useDebounce(data, loading ? 10 : 3000)
  useEffect(() => {
    setDebounced(data)//Ignore depenency becouse sideeffect is expected behavior
  }, [loading, setDebounced])
  const [option1Percentage, setOption1Pecentage] = useState(50);

  const [option2Percentage, setOption2Pecentage] = useState(
    100 - option1Percentage
  );

  const [isAnimatingNthLogo, setIsAnimatingNthLogo] = useState(false);

  const [optionThatGainsPercentages, setOptionThatGainsPercentages] =
    useState(0);

  const [flyingBunAnimating, setFlyingBunAnimating] = useState(false);

  const bun1AnimationProps = useSpring(
    createFlyingBunAnimationConfig(1)(flyingBunAnimating)(setFlyingBunAnimating)
  );

  const bun2AnimationProps = useSpring(
    createFlyingBunAnimationConfig(2)(flyingBunAnimating)(setFlyingBunAnimating)
  );

  const option1logoAnimationProps = useSpring(
    createLogoAnimationConfig(1)(isAnimatingNthLogo)(setIsAnimatingNthLogo)
  );

  const option2logoAnimationProps = useSpring(
    createLogoAnimationConfig(2)(isAnimatingNthLogo)(setIsAnimatingNthLogo)
  );

  useEffect(() => {
    const newOption1Percentage = Math.floor((debouncedData[product1] / (debouncedData[product1] + debouncedData[product2])) * 100) || 50;

    if (!isValidNumber(newOption1Percentage)) {
      return;
    }

    if (option1Percentage === newOption1Percentage) {
      return;
    }

    const newOptionThatGainsPercentages =
      newOption1Percentage > option1Percentage ? 1 : 2;

    setTimeout(
      () => setOption1Pecentage(newOption1Percentage),
      newOptionThatGainsPercentages === 1 ? 1250 : 0
    );

    setTimeout(
      () => setOption2Pecentage(100 - newOption1Percentage),
      newOptionThatGainsPercentages === 2 ? 1250 : 0
    );

    setIsAnimatingNthLogo(newOptionThatGainsPercentages);

    setOptionThatGainsPercentages(newOptionThatGainsPercentages);
  }, [loading, debouncedData, option1Percentage]);

  useEffect(() => {
    setFlyingBunAnimating(optionThatGainsPercentages);
  }, [optionThatGainsPercentages, option1Percentage]);

  return (
    <div className="app">
      <BunQueue setDelta={setDelta} productKey={product1} delta={delta} type="2" />
      <BunQueue setDelta={setDelta} productKey={product2} delta={delta} left="50%" type="1" />
      <div className="right-now" />

      <animated.div
        className="logo logo-1"
        style={{
          transform: option1logoAnimationProps.x
            .to({ range: [0, 0.5, 1], output: [2.125, 2.625, 2.125] })
            .to((x) => `scale(${x}) rotate(-7deg)`),
        }}
      />

      <animated.div
        className="logo logo-2"
        style={{
          transform: option2logoAnimationProps.x
            .to({ range: [0, 0.5, 1], output: [1.5, 2, 1.5] })
            .to((x) => `scale(${x}) rotate(7deg)`),
        }}
      />



      <animated.div
        className="bun moving-bun bun-1"
        style={createFlyingBunStyle(1)(bun1AnimationProps)}
      />

      <animated.div
        className="bun moving-bun bun-2"
        style={createFlyingBunStyle(2)(bun2AnimationProps)}
      />

      <Option nth={1} percentage={option1Percentage} />
      <Option nth={2} percentage={option2Percentage} />

      {mode === "test" && (
        <div className="fixed">
          <button
            onClick={() =>
              increaseDemoData(1)(fsPath)
            }
          >
            Increase option 1
          </button>

          <button
            onClick={() =>
              increaseDemoData(2)(fsPath)
            }
          >
            Increase option 2
          </button>

          <button
            onClick={() => resetDemoData(fsPath)}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default App;
